import React, { useState } from 'react';
import { useOutletContext, useNavigate, useLocation } from 'react-router-dom';
import RegistrationCodes from '../components/RegistrationCodes';
import CloudflareManager from '../components/CloudflareManager';
import TutorialModal from '../components/TutorialModal';
import AdminLLMs from './AdminLLMs';
import AdminBobSettings from './AdminBobSettings';
import AdminIntegrations from './AdminIntegrations';
import { hasSeenTutorial } from '../utils/tutorialData';
import {
    KeyIcon as Key,
    EmailIcon as Mail,
    ActivityIcon as AnalyticsIcon,
    ArrowLeftIcon as ArrowLeft,
    ChevronRightIcon as ChevronRight,
    SettingsIcon as Settings,
    GlobeIcon as Globe,
    SparklesIcon,
    KeyIcon as SecretsIcon
} from '../components/Icons';

const AdminTools = () => {
    const { profile } = useOutletContext();
    const navigate = useNavigate();
    const location = useLocation();
    const [showTutorial, setShowTutorial] = useState(!hasSeenTutorial('admin-tools'));

    if (!profile || (profile.role !== 'admin' && profile.role !== 'gruppenleiter')) {
        return (
            <div className="container mx-auto p-4 max-w-2xl">
                <div className="bg-error-container text-error-on-container p-6 rounded-xl text-center shadow-sm">
                    <h2 className="text-headline-small font-bold mb-2">Zugriff verweigert</h2>
                    <p className="opacity-90">Sie müssen Admin oder Gruppenleiter sein, um diese Seite zu sehen.</p>
                </div>
            </div>
        );
    }

    const isSubPage = location.pathname !== '/admin-tools';

    const categories = [
        {
            id: 'integrations',
            title: 'Schnittstellen & Secrets',
            description: 'Aktiviere/deaktiviere APIs (Gemini, Resend, Cloudflare, Google OAuth, Analytics) und verwalte Keys.',
            icon: <SecretsIcon sx={{ fontSize: 24 }} />,
            color: 'bg-primary-container text-primary-onContainer',
            adminOnly: true
        },
        {
            id: 'codes',
            title: 'Registrierungscodes',
            description: 'Erstelle und verwalte Einladungscodes für neue Mitglieder.',
            icon: <Key sx={{ fontSize: 24 }} />,
            color: 'bg-secondary-container text-secondary-onContainer',
            adminOnly: true
        },
        {
            id: 'emails',
            title: 'E-Mail Weiterleitungen',
            description: 'Verwalte Cloudflare E-Mail-Adressen und Weiterleitungen.',
            icon: <Mail sx={{ fontSize: 24 }} />,
            color: 'bg-tertiary-container text-tertiary-onContainer',
            adminOnly: true
        },
        {
            id: 'homepage',
            title: 'Startseite & Inhalte',
            description: 'Passe Inhalte, Sektionen-Reihenfolge, Bilder und Design der Startseite an.',
            icon: <Globe sx={{ fontSize: 24 }} />,
            color: 'bg-primary-container text-primary-onContainer',
            adminOnly: false
        },
        {
            id: 'analytics',
            title: 'Analytics',
            description: 'Besucherstatistiken und Berichte in Google Analytics einsehen.',
            icon: <AnalyticsIcon sx={{ fontSize: 24 }} />,
            color: 'bg-secondary-container text-secondary-onContainer',
            adminOnly: false
        },
        {
            id: 'llms',
            title: 'Robots & KI-Metadaten',
            description: 'Verwalte die robots.txt für Suchmaschinen sowie llms.txt für KI-Assistenten.',
            icon: <Globe sx={{ fontSize: 24 }} />,
            color: 'bg-tertiary-container text-tertiary-onContainer',
            adminOnly: true
        },
        {
            id: 'bob',
            title: 'Bob & Prompts',
            description: 'Verwalte Bobs System-Prompts und dynamische Wissens-Einträge.',
            icon: <SparklesIcon sx={{ fontSize: 24 }} />,
            color: 'bg-primary-container text-primary-onContainer',
            adminOnly: true
        }
    ];

    if (isSubPage) {
        const path = location.pathname;
        let content;
        let title;

        if (path.includes('/integrations')) {
            title = 'Schnittstellen & Secrets';
            content = <AdminIntegrations />;
        } else if (path.includes('/codes')) {
            title = 'Registrierungscodes';
            content = <RegistrationCodes />;
        } else if (path.includes('/emails')) {
            title = 'E-Mail Weiterleitungen';
            content = <CloudflareManager />;
        } else if (path.includes('/llms') || path.includes('/robots')) {
            title = 'Robots & KI-Metadaten (robots.txt & llms.txt)';
            content = <AdminLLMs />;
        } else if (path.includes('/bob')) {
            title = 'Bob & Prompts';
            content = <AdminBobSettings />;
        } else if (path.includes('/analytics')) {
            title = 'Google Analytics';
            content = (
                <div className="space-y-6">
                    <p className="text-body-large text-surface-onVariant">Hier kannst du direkt auf das Google Analytics Dashboard zugreifen.</p>
                    <a
                        href="https://analytics.google.com/analytics/web/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-between p-8 bg-surface-containerHigh rounded-3xl border border-outlineVariant hover:bg-tertiary-container hover:border-tertiary transition-all group shadow-sm"
                    >
                        <div className="flex flex-col gap-1">
                            <span className="text-headline-small font-bold text-surface-on group-hover:text-tertiary-onContainer transition-colors">Google Analytics öffnen</span>
                            <span className="text-body-large text-surface-onVariant group-hover:text-tertiary-onContainer/80 transition-colors">Besucherstatistiken & Berichte</span>
                        </div>
                        <div className="w-16 h-16 rounded-full bg-surface-containerHighest flex items-center justify-center text-surface-onVariant group-hover:bg-tertiary group-hover:text-tertiary-on transition-all shadow-sm">
                            <ChevronRight sx={{ fontSize: 32 }} />
                        </div>
                    </a>
                </div>
            );
        }

        return (
            <div className="flex flex-col animate-fade-in h-full">
                <header className="sticky top-0 z-30 bg-surface/80 backdrop-blur-md border-b border-outlineVariant/50 px-4 py-4 md:px-8">
                    <div className="flex items-center gap-4">
                        <button
                            onClick={() => navigate('/admin-tools')}
                            className="w-10 h-10 rounded-full flex items-center justify-center hover:bg-surface-on/5 text-surface-on transition-colors shrink-0"
                        >
                            <ArrowLeft sx={{ fontSize: 24 }} />
                        </button>
                        <div>
                            <h1 className="text-title-large font-bold text-surface-on">{title}</h1>
                            <p className="text-label-small text-surface-onVariant uppercase tracking-widest font-bold opacity-60">Admin-Tool</p>
                        </div>
                    </div>
                </header>
                <main className="flex-1 p-4 md:p-8 w-full">
                    <div className="md3-card-elevated p-6 md:p-10 rounded-[40px] border border-outlineVariant/50 bg-surface-containerLowest shadow-sm">
                        {content}
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 md:p-8 max-w-6xl animate-fade-in">
            {showTutorial && (
                <TutorialModal pageId="admin-tools" onClose={() => setShowTutorial(false)} />
            )}

            <div className="mb-12">
                <div className="flex items-center gap-3 text-primary mb-2">
                    <Settings sx={{ fontSize: 32 }} />
                    <h1 className="text-display-small font-black tracking-tight">Admin-Tools</h1>
                </div>
                <p className="text-body-large text-surface-onVariant">Wähle einen Bereich aus, den du verwalten möchtest.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {categories.map((cat) => {
                    if (cat.adminOnly && profile.role !== 'admin') return null;

                    return (
                        <button
                            key={cat.id}
                            onClick={() => navigate(`/admin-tools/${cat.id}`)}
                            className="md3-card-elevated p-8 text-left hover:border-primary/50 transition-all group flex flex-col h-full border border-outlineVariant/30"
                        >
                            <div className={`w-14 h-14 rounded-[20px] ${cat.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform shadow-sm`}>
                                {cat.icon}
                            </div>
                            <h3 className="text-title-large font-bold text-surface-on mb-2 group-hover:text-primary transition-colors">
                                {cat.title}
                            </h3>
                            <p className="text-body-medium text-surface-onVariant flex-1">
                                {cat.description}
                            </p>
                            <div className="mt-6 flex items-center text-primary font-bold text-label-large gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                Öffnen
                                <ChevronRight sx={{ fontSize: 18 }} />
                            </div>
                        </button>
                    );
                })}
            </div>
        </div>
    );
};

export default AdminTools;
